<script setup lang="ts">
  // @ts-nocheck
  import { ref, computed, onMounted, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { formatDate } from '@qic/ui-components'
  import { convertStartDateToTimestamp } from '@qicgroupdoha/travel-devkit/helpers/dates'
  import { BaseTabs } from '@/shared/components'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import { useUserStore, useCommonStore } from '@/shared/store'
  import { usePerformanceTiming, useValidation, useValidators } from '@/shared/composables'
  import {
    MAX_DEPARTURE_DAY_WITHOUT_TODAY,
    MAX_DEPARTURE_DAY_WITHOUT_TODAY_OUTBOUND,
    MAX_DEPARTURE_DAY_WITHOUT_TODAY_USA,
    MAX_RETURN_DAY,
    PROCEED_EVENT_NAME,
    LOAD_EVENT_NAME,
  } from './FlowTravelDatesWidget.constants'

  import { TravelType } from '@/shared/types/api.types'
  import BaseDatePicker from '@/shared/components/BaseDatePicker/BaseDatePicker.vue'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import { OUTBOUND_MULTI_TRIP_RETURN_DAY } from '@/shared/constants/plans.constants'

  // TODO: Split in 2 components: SingleTripTab and MultiTripTab

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>): void
  }

  interface Props {
    typeOfTravel?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
  })

  const emit = defineEmits<Emits>()

  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { t, locale } = useI18n()
  const gaService = useGoogleAnalytics()
  const { onPerformanceCheckEnd } = usePerformanceTiming()

  const isAllWorld = !!userStore.destination.find((el) => el.id === '999')
  const isUsaOrCanada = userStore.destination.some((el) => ['082', '020'].includes(el.id))
  const tabs = [
    {
      title: t('outbound_travel_type_single'),
      subtitle: t(
        isAllWorld || isUsaOrCanada
          ? 'outbound_travel_type_single_desc_60'
          : 'outbound_travel_type_single_desc'
      ),
      id: 'tab1',
    },
    {
      title: t('outbound_travel_type_multi'),
      subtitle: t('outbound_travel_type_multi_desc'),
      id: 'tab2',
    },
  ]

  const popupAnnotation = t(
    isAllWorld || isUsaOrCanada
      ? 'outbound.calendar.trip_length_60'
      : 'outbound.calendar.trip_length'
  )

  const activeTab = ref(userStore.tripType === 'SINGLE' ? tabs[0].id : tabs[1].id)
  const departureDate = ref<Date | null>(
    userStore.tripType === 'SINGLE' ? userStore.departureDate : null
  )
  const returnDate = ref<Date | null>(userStore.tripType === 'SINGLE' ? userStore.returnDate : null)

  const multiDepartureDate = ref<Date | null>(
    userStore.tripType !== 'SINGLE' ? userStore.departureDate : null
  )

  const singleTripDates = computed({
    get: () => {
      return {
        start: departureDate.value,
        end: returnDate.value,
      }
    },
    set: (dates) => {
      departureDate.value = dates?.start || ''
      returnDate.value = dates?.end || ''
    },
  })

  const isInboud = props.typeOfTravel === TravelDirection.INBOUND
  const isSingleTrip = computed(() => isInboud || activeTab.value === tabs[0].id)

  const { required } = useValidators()
  const validationRules = computed(() => {
    if (isSingleTrip.value) {
      return {
        departureDate: {
          required,
        },
        returnDate: {
          required,
        },
      }
    } else {
      return {
        multiDepartureDate: {
          required,
        },
      }
    }
  })

  const { v$, errors } = useValidation(validationRules, {
    departureDate,
    returnDate,
    multiDepartureDate,
  })

  const onProceed = async () => {
    v$.value.$touch()
    if (!v$.value.$error) {
      emit(LOAD_EVENT_NAME, async () => {
        const formattedDepartureDate = isSingleTrip.value
          ? departureDate.value
          : multiDepartureDate.value // This line needs to correct TS working
        if (formattedDepartureDate === null) return

        let formattedReturnDate: Date | null
        if (isSingleTrip.value) {
          formattedReturnDate = returnDate.value
        } else {
          formattedReturnDate = new Date(
            new Date(formattedDepartureDate).setDate(
              formattedDepartureDate.getDate() + OUTBOUND_MULTI_TRIP_RETURN_DAY
            )
          )
        }

        if (isSingleTrip.value && formattedReturnDate === null) return

        userStore.setTravelDates(formattedDepartureDate, formattedReturnDate, true)

        const tripType = isSingleTrip.value ? 'SINGLE' : 'MULTI'
        userStore.setTripType(tripType, true)
        userStore.setPrevTripType(tripType, true)

        const data = {
          policyDate: convertStartDateToTimestamp(formattedDepartureDate), // Send as unix timestamp
          endDate:
            formattedReturnDate !== null
              ? convertStartDateToTimestamp(formattedReturnDate)
              : formattedReturnDate, // Send as unix timestamp
          travelType: isSingleTrip.value ? TravelType.SINGLE : TravelType.MULTITRIP,
        }

        if (isInboud && userStore.residenceCountry) {
          data['originCode'] = userStore.residenceCountry.id
          data['originName'] = userStore.residenceCountry.name
        }

        await commonStore.sendFormData(data)
        gaService.sendTravelDatesProceed(
          props.typeOfTravel,
          isSingleTrip.value ? 'single' : 'multi',
          formattedDepartureDate,
          formattedReturnDate,
          userStore.destination?.map((country) => country?.id || '')
        )

        emit(PROCEED_EVENT_NAME)
      })
    }
  }

  const handleChange = () => {
    // returnDate.value = null;
    // if (departureDate.value === null){
    //   departureDate.value = new Date();
    // }
  }

  const mediaQuery = window.matchMedia(`(max-width: 550px)`)
  const isMobile = ref(mediaQuery.matches)
  const handleResize = (event: MediaQueryListEvent) => {
    isMobile.value = event.matches
  }

  onMounted(() => {
    mediaQuery.addEventListener('change', handleResize)
    onPerformanceCheckEnd()
  })

  onUnmounted(() => {
    mediaQuery.removeEventListener('change', handleResize)
  })

  const minDepartureDate = new Date()
  const maxMultiDepartureDate = computed(() => {
    const today = new Date()
    return new Date(today.setDate(today.getDate() + MAX_RETURN_DAY))
  })

  const onInput = (event) => {
    const newValue = event.target.value
    if (newValue.length < 10) return

    const [day, month, year] = event.target.value.split('/')
    if (day.length != 2 || month.length != 2 || year.length != 4) return // After manual input we need to wait masked input, because we can get 21/022/023 instead of 21/02/2023

    const newDate = new Date(year, month - 1, day)
    const minDate = new Date()

    if (newDate > maxMultiDepartureDate.value || newDate < minDate) return
    departureDate.value = newDate
  }

  const focusedDate = ref<{ start: Date; end: Date }>()

  const maxDays = computed(() => {
    const focusedStartDate = focusedDate.value?.start
    let maxDays = isInboud
      ? MAX_DEPARTURE_DAY_WITHOUT_TODAY
      : MAX_DEPARTURE_DAY_WITHOUT_TODAY_OUTBOUND

    if (focusedStartDate) {
      maxDays = MAX_DEPARTURE_DAY_WITHOUT_TODAY
      if (!isInboud && (isAllWorld || isUsaOrCanada)) {
        maxDays = MAX_DEPARTURE_DAY_WITHOUT_TODAY_USA
      }
    }

    return maxDays
  })

  const minDate = computed(() => {
    const start = focusedDate.value?.start
    const end = focusedDate.value?.end
    let focusedStartDate

    if (start && end && start < end) {
      const dateMinus90Days = new Date(end.setDate(end.getDate() - maxDays.value))
      if (dateMinus90Days > minDepartureDate) {
        focusedStartDate = dateMinus90Days
      }
    }
    return focusedStartDate || minDepartureDate
  })

  const maxDate = computed(() => {
    const focusedStartDate = focusedDate.value?.start

    if (focusedStartDate) {
      return new Date(focusedStartDate.setDate(focusedStartDate.getDate() + maxDays.value))
    }

    const today = new Date()

    return new Date(today.setDate(today.getDate() + maxDays.value))
  })

  const maxMultiTripDate = computed(() => {
    if (isInboud) {
      return null
    }

    const today = new Date()
    return new Date(today.setDate(today.getDate() + MAX_DEPARTURE_DAY_WITHOUT_TODAY_OUTBOUND))
  })

  const handleDayClick = (date) => {
    focusedDate.value = date
  }

  const footerSlotName = computed(() => (isMobile.value ? 'fixed-footer' : 'footer'))

  const multitripAnnotation = computed(() => {
    if (!multiDepartureDate.value) {
      return ''
    }

    const startDate = multiDepartureDate.value
    const endDate = new Date(startDate.valueOf())
    endDate.setDate(endDate.getDate() + OUTBOUND_MULTI_TRIP_RETURN_DAY)
    return `${t('from')} ${formatDate(startDate, locale.value)} ${t('till')} ${formatDate(
      endDate,
      locale.value
    )}`
  })
</script>

<template>
  <!-- @vue-ignore -->
  <FlowCard :title="t('outbound_travel_type_header')" with-button @button-click="onProceed">
    <div class="travel-dates-content">
      <BaseTabs
        v-if="!isInboud"
        v-model="activeTab"
        :tabs="tabs"
        @tab-change="handleChange"
        size="big"
      />
      <div v-if="isSingleTrip" class="travel-dates-tab">
        <BaseDatePicker
          v-model="singleTripDates"
          is-range
          :start-date-label="t('inbound_calendar_departure')"
          :end-date-label="t('inbound_calendar_return')"
          :min-date="minDate"
          :max-date="maxDate"
          :locale="locale"
          :errors="{ start: errors.departureDate, end: errors.returnDate }"
          @day-focus="handleDayClick"
          @close="focusedDate = null"
          :mobile-title="t('outbound_travel_type_header')"
          @input="onInput"
        >
          <template v-slot:[footerSlotName]>
            <div class="flow-travel-dates-widget__multitrip-calendar-footer">
              {{ popupAnnotation }}
            </div>
          </template>
        </BaseDatePicker>
      </div>
      <div v-else>
        <BaseDatePicker
          v-model="multiDepartureDate"
          :start-date-label="t('outbound.calendar.first_departure')"
          :end-date-label="t('inbound_calendar_return')"
          :min-date="minDate"
          :max-date="maxMultiTripDate"
          :locale="locale"
          :errors="{ start: errors.multiDepartureDate }"
          @day-focus="handleDayClick"
          @close="focusedDate = null"
          :mobile-title="t('outbound_travel_type_header')"
          @input="onInput"
          :is-range="false"
        >
          <template v-slot:[footerSlotName]>
            <div class="flow-travel-dates-widget__multitrip-calendar-footer">
              {{ popupAnnotation }}
            </div>
          </template>
        </BaseDatePicker>

        <div v-if="multitripAnnotation" class="flow-travel-dates-widget__multitrip-annotation">
          <p>{{ multitripAnnotation }}</p>
          <p>{{ popupAnnotation }}</p>
        </div>
      </div>
    </div>
  </FlowCard>
</template>

<style scoped lang="scss">
  @import './FlowTravelDatesWidget';
</style>
